import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, useMediaQuery } from '@mui/material';
import actions from '@src/redux/actions';
import { ONBOARDING_POPUP_TYPE } from '@src/constants';
import { STATUS_SURVEY } from '@src/constants/survey';
import { useCheckEducatorsHomeRoute } from '@src/hooks/useCheckDubbingRoute';
import useEducatorsModeEnabled from './useEducatorsModeEnabled';

const ANSWERED_STATUSES = [STATUS_SURVEY.DONE, STATUS_SURVEY.SKIPPED];

/**
 * The welcome board that greets a teacher arriving in Educators mode. The survey
 * comes after it, so both the opening decision and the popup queue need to know
 * when it is on screen.
 *
 * Every hook here answers no while the mode is switched off. Without that, an
 * account with no Educators mode that reached the route still had the layout
 * hold back popups on behalf of a queue that could never run.
 */
export const useIsEducatorsWelcomeVisible = () => {
  const isEducatorsModeEnabled = useEducatorsModeEnabled();
  const { isVisible, type } = useSelector((state) => state.onboardingPopup);

  return (
    isEducatorsModeEnabled &&
    isVisible &&
    type === ONBOARDING_POPUP_TYPE.EDUCATORS
  );
};

const welcomeStorageKey = (userId) =>
  `ONBOARDING_POPUP_${ONBOARDING_POPUP_TYPE.EDUCATORS}_${userId}`;

/**
 * Whether the welcome board still owes this user an appearance — either it is on
 * screen or it has yet to open at all.
 *
 * Asking only whether it is on screen is not enough. The Educators home opens it
 * on mount, so arriving from another page leaves one render where the board is
 * due but not yet showing, and a survey that had everything else it needed would
 * open into that gap and end up stacked underneath the board a moment later.
 *
 * The Educators home reads the same key to make its decision and writes it as it
 * opens the board. Reading it here is safe because that write comes with a
 * dispatch, so a re-render always follows.
 */
export const useIsEducatorsWelcomeDue = () => {
  const isEducatorsModeEnabled = useEducatorsModeEnabled();
  const userId = useSelector((state) => state.auth.user?.id);
  const isWelcomeVisible = useIsEducatorsWelcomeVisible();

  if (isWelcomeVisible) return true;
  if (!isEducatorsModeEnabled || !userId) return false;

  return !localStorage.getItem(welcomeStorageKey(userId));
};

/**
 * Whether the welcome board is holding the queue right now — it is due, and the
 * user is somewhere it can actually open.
 *
 * The route matters. The board is opened by the Educators home, so away from it
 * the key is never written and "due" would stay true forever; anything waiting
 * on this would wait for good.
 */
export const useIsEducatorsWelcomeHolding = () => {
  const isEducatorsHomeRoute = useCheckEducatorsHomeRoute();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWelcomeDue = useIsEducatorsWelcomeDue();

  return isEducatorsHomeRoute && !isMobile && isWelcomeDue;
};

/**
 * Whether this session is still on course to see the survey. Only a record
 * saying done or skipped rules it out, so the answer is yes from the first
 * render on the Educators home — while the record is still on its way, and while
 * the session is still working through the shared onboarding.
 *
 * Deliberately blind to how far onboarding has got. Opening the survey waits on
 * a few things, but holding other popups back cannot: they open on nothing more
 * than a user id, so anything the hold overlooks gets a window to appear in and
 * then vanish from.
 */
export const useIsEducatorsSurveyPending = ({ userSurvey }) => {
  const isEducatorsModeEnabled = useEducatorsModeEnabled();
  const isEducatorsHomeRoute = useCheckEducatorsHomeRoute();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { answeredStatus } = useSelector((state) => state.educatorsSurvey);

  const hasAnswered =
    Boolean(answeredStatus) ||
    ANSWERED_STATUSES.includes(userSurvey?.educator?.status);

  return (
    isEducatorsModeEnabled && isEducatorsHomeRoute && !isMobile && !hasAnswered
  );
};

/**
 * Opens the Educators survey when this session is due one. It runs in the
 * layout because everything it weighs — how far the user is through onboarding,
 * whether a blocking dialog is up — lives there, while the popup itself is
 * rendered by the Educators home.
 *
 * Opening waits for the account's survey record even though the survey counts as
 * pending without it: putting the questions in front of someone who answered
 * them months ago is worse than a short wait. It also waits for the welcome
 * board, which greets the teacher first.
 *
 * It does not wait for the shared onboarding to finish. The queue now runs
 * ahead of the onboarding tasks, so waiting for them would leave each side
 * waiting on the other.
 */
const useEducatorsSurveyPopup = ({ userSurvey, hasBlockingPopup }) => {
  const dispatch = useDispatch();
  const isPending = useIsEducatorsSurveyPending({ userSurvey });
  const isWelcomeDue = useIsEducatorsWelcomeDue();

  const canOpen =
    isPending && Boolean(userSurvey) && !hasBlockingPopup && !isWelcomeDue;

  useEffect(() => {
    if (!canOpen) return;

    dispatch(actions.educatorsSurvey.showEducatorsSurvey());
  }, [canOpen, dispatch]);
};

export default useEducatorsSurveyPopup;
